import { useCallback, useEffect, useRef } from 'react'
import { useLatestRef } from './useLatestRef'

type SwipeDirection = 'left' | 'right'

/**
 * Returns a callback ref. Attach it to the element you want to detect swipes
 * on; listeners follow the current DOM node automatically (re-mounting,
 * re-keying, prop-driven node swaps).
 *
 * Using native addEventListener (not React's onTouch*) so we can register
 * touchmove as non-passive and call preventDefault() once we know the gesture
 * is horizontal. Without that, iOS hijacks the touch as a page scroll and
 * fires touchcancel instead of touchend, breaking the gesture.
 */
export function useSwipe(
  onSwipe: (direction: SwipeDirection) => void,
  minDistance = 50,
) {
  const onSwipeRef = useLatestRef(onSwipe)

  const cleanupRef = useRef<(() => void) | null>(null)

  const refCallback = useCallback(
    (el: HTMLElement | null) => {
      // Detach from the previous node first.
      cleanupRef.current?.()
      cleanupRef.current = null
      if (!el) return

      let startX: number | null = null
      let startY: number | null = null
      let horizontal: boolean | null = null

      // If the touch starts inside a tab strip or any descendant that opted
      // out via [data-no-swipe], let the native interaction (scroll, tab
      // click) handle it instead of converting the gesture into a swipe.
      function startedInBlocked(target: EventTarget | null): boolean {
        const node = target instanceof Element ? target : null
        if (!node || !el) return false
        const blocker = node.closest('[role="tablist"], [data-no-swipe]')
        return !!blocker && el.contains(blocker)
      }

      function handleStart(e: TouchEvent) {
        if (startedInBlocked(e.target)) {
          startX = null
          startY = null
          horizontal = null
          return
        }
        startX = e.touches[0].clientX
        startY = e.touches[0].clientY
        horizontal = null
      }

      function handleMove(e: TouchEvent) {
        if (startX === null || startY === null) return
        const dx = Math.abs(e.touches[0].clientX - startX)
        const dy = Math.abs(e.touches[0].clientY - startY)
        if (horizontal === null && dx + dy > 5) {
          horizontal = dx > dy
        }
        if (horizontal) e.preventDefault()
      }

      function handleEnd(e: TouchEvent) {
        if (startX === null || startY === null) return
        const dx = e.changedTouches[0].clientX - startX
        const dy = e.changedTouches[0].clientY - startY
        startX = null
        startY = null
        horizontal = null
        if (Math.abs(dx) >= minDistance && Math.abs(dx) > Math.abs(dy)) {
          onSwipeRef.current(dx < 0 ? 'left' : 'right')
        }
      }

      el.addEventListener('touchstart', handleStart, { passive: true })
      el.addEventListener('touchmove', handleMove, { passive: false })
      el.addEventListener('touchend', handleEnd, { passive: true })

      cleanupRef.current = () => {
        el.removeEventListener('touchstart', handleStart)
        el.removeEventListener('touchmove', handleMove)
        el.removeEventListener('touchend', handleEnd)
      }
    },
    [minDistance, onSwipeRef],
  )

  // Final cleanup on unmount.
  useEffect(() => () => cleanupRef.current?.(), [])

  return refCallback
}
